import { useMutation } from "@apollo/client/react";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { Form, Input, Modal } from "antd";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { CreateRecipeDocument } from "../../gql/generated/graphqlSchema.tsx";
import { routes } from "../../routes.ts";

type FormValues = {
  name: string;
};

export const CreateRecipeModal = ({
  open,
  onClose,
  initialName = "",
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly initialName?: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();

  const [createRecipe, { loading }] = useMutation(CreateRecipeDocument, {
    onCompleted: (data) => {
      onClose();
      void navigate(routes.recipeDetail.to(data.createRecipe.id));
    },
  });

  const handleOk = () => {
    form.validateFields().then((values) => {
      void createRecipe({ variables: { input: { name: values.name, description: null } } });
    });
  };

  return (
    <Modal
      afterClose={() => form.resetFields()}
      afterOpenChange={(visible) => {
        if (visible) form.setFieldsValue({ name: initialName });
      }}
      cancelButtonProps={{ icon: <CloseOutlined /> }}
      confirmLoading={loading}
      destroyOnHidden
      okButtonProps={{ icon: <PlusOutlined /> }}
      okText={t("common.create")}
      onCancel={onClose}
      onOk={handleOk}
      open={open}
      title={t("recipes.createRecipe")}
    >
      <Form form={form} layout="vertical" onFinish={handleOk}>
        <Form.Item
          label={t("recipes.name")}
          name="name"
          rules={[{ required: true, message: t("recipes.validation.nameRequired") }]}
        >
          <Input autoFocus placeholder={t("recipes.namePlaceholder")} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
